import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { readStoredAccessToken, writeAccessToken } from './requestCredential';
import { isCanonicalAuthRejection } from './authRejection';

const client = axios.create({ baseURL: API_BASE_URL, withCredentials: true,
  headers: { 'X-Mestti-CSRF': '1', 'X-Session-Client': 'WEB' }, timeout: 15000 });
let generation = 0;
export const sessionIdentityEpoch = (): number => generation;
let refreshFlight: Promise<any> | null = null;
let pendingRequestId: string | null = null;
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('mestti-auth') : null;
const LOGOUT_MARKER = 'mestti-explicit-logout';
let memoryLogoutMarker: string | null = null;
export type SessionChange = { type: 'LOGIN_CHANGED' } | { type: 'LOGOUT'; id: string };

function readLogoutMarker(): string | null {
  try { return localStorage.getItem(LOGOUT_MARKER); } catch { return memoryLogoutMarker; }
}
export const isExplicitlyLoggedOut = (): boolean => readLogoutMarker() !== null;
function storeLogoutMarker(marker: string | null): void {
  memoryLogoutMarker = marker;
  try {
    if (marker === null) localStorage.removeItem(LOGOUT_MARKER);
    else localStorage.setItem(LOGOUT_MARKER, marker);
  } catch { /* Memory + BroadcastChannel still end this page's identity. */ }
}
function announce(change: SessionChange): void {
  window.dispatchEvent(new CustomEvent('mestti-session-changed', { detail: change }));
}
function receive(change: SessionChange): void {
  if (change?.type !== 'LOGOUT' && change?.type !== 'LOGIN_CHANGED') return;
  let marker: string | null;
  try { marker = localStorage.getItem(LOGOUT_MARKER); } catch {
    // Storage may be disabled; typed broadcasts still end the other open tabs.
    memoryLogoutMarker = change.type === 'LOGOUT' ? change.id : null;
    marker = memoryLogoutMarker;
  }
  if (change?.type === 'LOGOUT') {
    if (marker !== change.id) return; // A subsequent login supersedes this message.
  } else if (marker) return;
  clearAccessSession();
  announce(change);
}

export function clearAccessSession(): void {
  generation += 1;
  refreshFlight = null;
  writeAccessToken(null);
  pendingRequestId = null;
}
channel?.addEventListener('message', event => receive(event.data));
window.addEventListener('storage', event => {
  if (event.key !== LOGOUT_MARKER) return;
  memoryLogoutMarker = event.newValue;
  if (event.newValue) receive({ type: 'LOGOUT', id: event.newValue });
  else if (!channel) receive({ type: 'LOGIN_CHANGED' });
});

function endAccessSession(): void {
  const id = crypto.randomUUID();
  storeLogoutMarker(id);
  clearAccessSession();
  const change: SessionChange = { type: 'LOGOUT', id };
  announce(change);
  channel?.postMessage(change);
}

async function locked<T>(action: () => Promise<T>): Promise<T> {
  const locks = (navigator as any).locks;
  return locks ? locks.request('mestti-session-refresh', action) : action();
}
function accept(data: any, epoch: number, loginMarker?: string | null): any {
  if (epoch !== generation || (loginMarker === undefined
    ? isExplicitlyLoggedOut() : readLogoutMarker() !== loginMarker)) {
    throw new Error('Session identity changed');
  }
  if (!data?.access_token || !data?.user?.id || !data?.user?.company_id) {
    throw new Error('Invalid session response');
  }
  writeAccessToken(data.access_token);
  pendingRequestId = null;
  return data;
}
export async function loginSession(email: string, password: string): Promise<any> {
  clearAccessSession();
  const epoch = generation;
  const loginMarker = readLogoutMarker();
  return locked(async () => {
    const data = new URLSearchParams({ username: email, password });
    const result = await client.post('/sessions/login', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    const accepted = accept(result.data, epoch, loginMarker);
    storeLogoutMarker(null); // Failed or stale login must never erase explicit logout.
    channel?.postMessage({ type: 'LOGIN_CHANGED' } satisfies SessionChange);
    return accepted;
  });
}
export function refreshSession(): Promise<any> {
  if (isExplicitlyLoggedOut()) return Promise.reject(new Error('Explicit logout'));
  if (refreshFlight) return refreshFlight;
  const epoch = generation;
  const flight = locked(async () => {
    if (epoch !== generation || isExplicitlyLoggedOut()) throw new Error('Session identity changed');
    pendingRequestId = pendingRequestId || crypto.randomUUID();
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const result = await client.post('/sessions/refresh', { request_id: pendingRequestId });
        return accept(result.data, epoch);
      } catch (error: any) {
        if (epoch !== generation) throw error;
        if ((error.response?.status === 409 || !error.response || error.response?.status >= 500) && attempt === 0) {
          // Another tab rotated the shared cookie. Retry with the browser's current
          // cookie, retaining our UUID; no automatic logout for rotation contention.
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        if (isCanonicalAuthRejection(error)) endAccessSession();
        throw error;
      }
    }
  });
  refreshFlight = flight;
  void flight.finally(() => { if (refreshFlight === flight) refreshFlight = null; }).catch(() => {});
  return flight;
}
export async function ensureAccessSession(): Promise<string | null> {
  if (isExplicitlyLoggedOut()) { clearAccessSession(); return null; }
  const current = readStoredAccessToken();
  if (!current) return null; // Bootstrap explicitly establishes identity first.
  try {
    const payload = JSON.parse(atob(current.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.exp * 1000 - Date.now() > 60_000) return current;
  } catch { /* Refresh malformed/expired access from the independent cookie. */ }
  await refreshSession();
  return readStoredAccessToken();
}
export async function logoutSession(): Promise<void> {
  endAccessSession();
  await locked(async () => { await client.post('/sessions/logout', {}); });
}
export async function requestWsTicket(endpoint: string): Promise<string> {
  const epoch = generation;
  const token = await ensureAccessSession();
  if (epoch !== generation) throw new Error('Session identity changed');
  if (!token) throw new Error('Session absent');
  const response = await client.post('/sessions/ws-ticket', { endpoint }, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (epoch !== generation) throw new Error('Session identity changed');
  return response.data.ticket;
}

/** Retry only a rejection made before a domain handler runs. Never retry a pinned
 * credential from another identity or repeat a domain 401. */
export function installSessionRecovery(api: any): void {
  api.interceptors.response.use((response: any) => response, async (error: any) => {
    const config = error.config;
    if (!isCanonicalAuthRejection(error) || !config) throw error;
    const sent = config.headers?.Authorization || config.headers?.authorization;
    const current = readStoredAccessToken();
    if (!current || sent !== `Bearer ${current}` || config.authorizationPinned) {
      throw error;
    }
    if (config.sessionRetried) {
      endAccessSession();
      throw error;
    }
    const epoch = generation;
    try {
      await refreshSession();
      if (epoch !== generation) throw new Error('Session identity changed');
      return api.request({ ...config, sessionRetried: true,
        headers: { ...config.headers, Authorization: `Bearer ${readStoredAccessToken()}` } });
    } catch {
      // Canonical refresh rejection owns lifecycle; downstream failures preserve it.
      throw error;
    }
  });
}
